import { useGetHeliosBrowseItemsQuery, useGetHeliosDataTablesQuery } from 'store/services/helios/dataHelios';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormSetValue } from 'react-hook-form';
import { Box } from '@mui/material';

import { Table } from 'components/common/Table/Table';
import { ActionModal } from 'components/mui/common/ActionModal';
import { HeliosBrowseFilterControls } from 'components/user/DummyHelios/Listing/components/HeliosBrowseFilterControls';
import { useTableQueryControls } from 'utils/hooks';
import { useHeliosBrowseFilters } from 'utils/hooks/userAgendaTable/useHeliosBrowseFilters';
import type { HeliosRecordDataTablesColumn } from 'types/dataHelios';
import { generateTableSchemaAndData, type HeliosSchemeType } from 'utils/table/user/helios/listing';
import { browseIdsAfterAutoSelect, mergeBrowsePageSelection } from 'utils/table/user/helios/mergeBrowsePageSelection';

import type { ModalsType } from 'types';
import type { HeliosCellRawValue } from 'types/helios/agendaListing';
import { SearchField } from 'components/common/SearchField';
import { computeDisplayColumnsTextFromRow } from 'utils/helios/displayColumns';
import { isMobileApp } from 'utils/common';

interface RowProps {
  id: string;
  name: string;
}

const selectDrawerTableSx = {
  '& .MuiTableContainer-root': {
    height: 'auto !important',
    minHeight: '0 !important',
    maxHeight: 'calc(90vh - 300px) !important',
    overflowY: 'auto !important',
  },
} as const;

interface Props<K extends FieldValues> extends ModalsType {
  setValue: UseFormSetValue<K>;
  fieldName: Path<K>;
  codeListTemplateId?: string;
  dynamicRelationId?: number;
  templateItemId?: number;
  templateViewId?: string;
  recordNumber?: number | null;
  value?: HeliosCellRawValue | null;
  isNew: boolean;
  changeValue?: (value: boolean) => void;
  valueItemId?: number;
  displayColumns?: Array<{ itemId: number; order: number }>;
  allowMultipleSelection?: boolean;
  dataItems?: { itemId: string; value: string }[];
  initialSearch?: string;
  isAutoSelectSearchResultEnabled?: boolean;
}

export const BrowseEditModal = memo(
  <K extends FieldValues>({
    open,
    onClose,
    setValue,
    fieldName,
    codeListTemplateId,
    dynamicRelationId,
    templateItemId,
    templateViewId,
    recordNumber,
    value,
    isNew,
    changeValue,
    valueItemId,
    displayColumns,
    allowMultipleSelection,
    dataItems,
    initialSearch,
    isAutoSelectSearchResultEnabled,
  }: Props<K>) => {
    const { t } = useTranslation();
    const selectedRows = useRef<RowProps[]>([]);
    const [currentSelection, setCurrentSelection] = useState<RowProps[]>([]);

    const isSingleBrowse = !dynamicRelationId && !allowMultipleSelection;

    const { state, setPagination, setSearch, setSort } = useTableQueryControls<HeliosSchemeType, Record<string, string>>(
      `HeliosBrowseEdit-${codeListTemplateId}`,
      {
        filters: {},
        search: '',
        pagination: { page: 1, pageSize: 10 },
      },
      { syncPaginationWithUrl: false }
    );

    const { data: browseItems, isFetching: isFetchingBrowseItems } = useGetHeliosBrowseItemsQuery(
      {
        templateId: templateViewId ?? '',
        templateItemId: templateItemId?.toString() ?? '',
        recordId: recordNumber?.toString() ?? '',
      },
      { skip: !open || isSingleBrowse || isNew }
    );

    const prioritizeRecordIds = useMemo(() => {
      if (!open || isSingleBrowse) {
        return undefined;
      }

      const extractIdFromValue = (val: HeliosCellRawValue): number | null => {
        if (typeof val === 'object' && val !== null && 'id' in val && val.id) {
          return Number(val.id);
        }
        return null;
      };

      if (Array.isArray(value) && value.length > 0) {
        const ids = value.map(extractIdFromValue).filter((id): id is number => id !== null && !isNaN(id));
        return ids.length > 0 ? ids : undefined;
      }

      if (browseItems && browseItems.length > 0) {
        const ids = browseItems.map((item) => Number(item.recordNumber)).filter((id) => !isNaN(id));
        return ids.length > 0 ? ids : undefined;
      }

      return undefined;
    }, [browseItems, isSingleBrowse, open, value]);

    const sortBy = state?.sort?.by ? String(state.sort.by.replace(/__DOT__/g, '.')) : '';
    const columnsRef = useRef<HeliosRecordDataTablesColumn[] | undefined>(undefined);
    const browsePageSize = state?.pagination?.pageSize ?? 10;
    const browseFilters = useHeliosBrowseFilters({
      templateId: open ? (codeListTemplateId ?? '') : '',
      open,
      columnsRef,
      setPagination,
      pageSize: browsePageSize,
    });

    const { data, isFetching } = useGetHeliosDataTablesQuery(
      {
        templateId: codeListTemplateId ?? '',
        filters: browseFilters.apiFilters,
        formData: {
          dataItems: dataItems || [],
          templateId: templateViewId ?? '',
          itemId: templateItemId ?? null,
          recordId: recordNumber ?? null,
        },
        searchKeyword: state?.search ?? '',
        offset: ((state?.pagination?.page ?? 1) - 1) * (state?.pagination?.pageSize ?? 10),
        limit: state?.pagination?.pageSize ?? 10,
        orderBy: sortBy,
        orderDesc: state?.sort?.order === 'desc',
        useServiceAccount: false,
        prioritizeRecordIds,
      },
      {
        skip: !codeListTemplateId || !open,
      }
    );

    columnsRef.current = data?.columns ?? columnsRef.current;

    const { transformedData, tableScheme } = useMemo(() => {
      return generateTableSchemaAndData(data?.columns, data?.data);
    }, [data]);

    // Find the column key for the valueItemId
    const valueColumnKey = useMemo(() => {
      if (!valueItemId || !data?.columns) return null;
      const column = data.columns.find((col) => col.itemId === valueItemId);
      if (column) {
        return column.columnKey.replace('.', '__DOT__');
      }
      return null;
    }, [valueItemId, data?.columns]);

    const initialSelectedRows = useMemo(() => {
      if (!isSingleBrowse) {
        return (
          browseItems?.map((item) => ({
            id: item.recordNumber.toString(),
            name: item.name ?? '',
          })) ?? []
        );
      }

      if (!value) return [];

      const findRowById = (id: string | number) => {
        const rowIndex = transformedData.findIndex((row: Record<string, unknown>) => String(row.id) === String(id));
        return rowIndex !== -1 ? [{ id: String(transformedData[rowIndex].id || ''), name: String(transformedData[rowIndex].name || '') }] : [];
      };

      const findRowByValue = (searchValue: string | number) => {
        if (!valueColumnKey) return findRowById(searchValue);

        const rowIndex = transformedData.findIndex((row: Record<string, unknown>) => String(row[valueColumnKey]) === String(searchValue));
        return rowIndex !== -1 ? [{ id: String(transformedData[rowIndex].id || ''), name: String(transformedData[rowIndex].name || '') }] : [];
      };

      const isValidSearchValue = (val: unknown): val is string | number => {
        return typeof val === 'string' || typeof val === 'number';
      };

      if (typeof value === 'object' && value !== null) {
        if ('value' in value && isValidSearchValue(value.value)) {
          return findRowByValue(value.value);
        }
        if ('id' in value && value.id) {
          return findRowById(value.id);
        }
        return [];
      }

      if (isValidSearchValue(value)) {
        return findRowByValue(value);
      }

      return [];
    }, [browseItems, isSingleBrowse, value, transformedData, valueColumnKey]);

    const setSearchRef = useRef(setSearch);
    setSearchRef.current = setSearch;
    const hasInitializedSelectionRef = useRef(false);
    const [selectionTouched, setSelectionTouched] = useState(false);

    useEffect(() => {
      if (!open) {
        return;
      }

      setSearchRef.current(initialSearch ?? '');
    }, [initialSearch, open]);

    useEffect(() => {
      if (!open) {
        hasInitializedSelectionRef.current = false;
        if (selectionTouched) {
          setSelectionTouched(false);
        }
        return;
      }

      if (selectionTouched || hasInitializedSelectionRef.current) {
        return;
      }

      selectedRows.current = initialSelectedRows;
      setCurrentSelection(initialSelectedRows);

      if (initialSelectedRows.length > 0) {
        hasInitializedSelectionRef.current = true;
      }
    }, [initialSelectedRows, open, selectionTouched]);

    const handleClose = useCallback(() => {
      selectedRows.current = [];
      setCurrentSelection([]);
      setPagination({
        page: 1,
        pageSize: state?.pagination?.pageSize ?? 10,
      });
      onClose();
    }, [onClose, setPagination, state?.pagination?.pageSize]);

    const buildSingleBrowseValue = useCallback(() => {
      const selectedRow = selectedRows.current[0];
      let valueToSave: HeliosCellRawValue = selectedRow.id;
      let displayName: string = selectedRow.name;

      const selectedRowIndex = transformedData.findIndex((row: Record<string, unknown>) => String(row.id) === String(selectedRow.id));

      if (selectedRowIndex !== -1) {
        const displayColumnsText =
          displayColumns && displayColumns.length > 0 && data?.columns
            ? computeDisplayColumnsTextFromRow(transformedData[selectedRowIndex], data.columns, displayColumns)
            : null;

        if (displayColumnsText) {
          displayName = displayColumnsText;
        }

        if (valueItemId && valueColumnKey) {
          const valueFromColumn = transformedData[selectedRowIndex][valueColumnKey];
          if (valueFromColumn !== undefined) {
            valueToSave = valueFromColumn as HeliosCellRawValue;
          }
        }
      }

      return {
        id: selectedRow.id,
        name: displayName,
        value: valueToSave,
      };
    }, [transformedData, displayColumns, data?.columns, valueItemId, valueColumnKey]);

    const buildDynamicRelationValue = useCallback(() => {
      const originalBrowses = browseItems?.map((item) => item.recordNumber.toString()) || [];
      const selectedIds = selectedRows.current.map((row) => row.id);

      const addedBrowses = selectedRows.current.filter((row) => !originalBrowses.includes(row.id)).map((row) => ({ id: row.id, status: 'add' }));

      const removedBrowses = originalBrowses.filter((id) => !selectedIds.includes(id)).map((id) => ({ id, status: 'remove' }));

      return [...addedBrowses, ...removedBrowses];
    }, [browseItems]);

    const buildMultiSelectValue = useCallback(() => {
      const currentValue = Array.isArray(value) ? value : value ? [value] : [];
      const currentIds = currentValue.map((v) => (typeof v === 'object' && v !== null && 'id' in v ? String(v.id) : String(v)));

      const selectedIds = selectedRows.current.map((row) => row.id);

      const addedIds = selectedIds.filter((id) => !currentIds.includes(id));
      const removedIds = currentIds.filter((id) => !selectedIds.includes(id));

      const addedBrowses = addedIds.map((id) => ({ id, status: 'add' }));
      const removedBrowses = removedIds.map((id) => ({ id, status: 'remove' }));

      return [...addedBrowses, ...removedBrowses];
    }, [value]);

    const handleSubmit = useCallback(() => {
      if (isSingleBrowse && !selectedRows.current[0]) {
        return;
      }

      const browseValue = isSingleBrowse ? buildSingleBrowseValue() : dynamicRelationId ? buildDynamicRelationValue() : buildMultiSelectValue();

      // An empty add/remove diff means the selection matches the stored relations —
      // restore the previous value instead of writing `[]`, which `required`
      // validation treats as an empty value and would block saving.
      if (!isSingleBrowse && Array.isArray(browseValue) && browseValue.length === 0) {
        setValue(fieldName, value as K[typeof fieldName], { shouldDirty: true });
        handleClose();
        return;
      }

      changeValue?.(true);
      setValue(fieldName, browseValue as unknown as K[typeof fieldName], { shouldDirty: true });
      handleClose();
    }, [
      changeValue,
      isSingleBrowse,
      value,
      dynamicRelationId,
      buildSingleBrowseValue,
      buildDynamicRelationValue,
      buildMultiSelectValue,
      setValue,
      fieldName,
      handleClose,
    ]);

    const hasActiveBrowseFilters = browseFilters.activeFilters.length > 0;

    useEffect(() => {
      if (!open || !isAutoSelectSearchResultEnabled || hasActiveBrowseFilters || isFetching || transformedData.length !== 1) {
        return;
      }

      const firstRow = transformedData[0];
      const selectedRow: RowProps = {
        id: firstRow.id ?? '',
        name: firstRow.name ?? '',
      };

      if (!selectedRow.id) {
        return;
      }

      const nextSelection = mergeBrowsePageSelection(
        browseIdsAfterAutoSelect(selectedRows.current, selectedRow.id, isSingleBrowse),
        [selectedRow],
        selectedRows.current
      );

      selectedRows.current = nextSelection;
      setSelectionTouched(true);
      setCurrentSelection(nextSelection);
      handleSubmit();
    }, [open, isAutoSelectSearchResultEnabled, hasActiveBrowseFilters, isFetching, isSingleBrowse, transformedData, handleSubmit]);

    return (
      <ActionModal
        maxWidth="md"
        open={open}
        onClose={handleClose}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        isOnSubmitDisabled={isSingleBrowse && currentSelection.length === 0}
        title={t('common.select')}>
        <Box sx={isMobileApp() ? selectDrawerTableSx : undefined}>
          <Table
            rows={data?.count ?? 0}
            page={state?.pagination?.page ?? 1}
            pageSize={state?.pagination?.pageSize ?? 10}
            isLoading={isFetching || isFetchingBrowseItems}
            data={transformedData}
            dataSchema={tableScheme}
            onPageSizeChange={(pageSize) => {
              setPagination({
                page: 1,
                pageSize,
              });
            }}
            onCurrentPageChange={(page) => {
              setPagination({
                page,
                pageSize: state?.pagination?.pageSize ?? 10,
              });
            }}
            onSelectRow={(rowIds, rows) => {
              if (!rows) return;

              const nextSelection = mergeBrowsePageSelection(rowIds, rows as unknown as RowProps[], selectedRows.current);
              setSelectionTouched(true);
              selectedRows.current = nextSelection;
              setCurrentSelection(nextSelection);
            }}
            onSort={({ column, direction }) => {
              setSort({
                by: String(column),
                order: direction,
              });
            }}
            sort={{
              column: state?.sort?.by ?? '',
              direction: state?.sort?.order ?? 'asc',
            }}
            controls={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <SearchField
                  placeholder={t('common.search._')}
                  value={state?.search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  disabled={isFetching}
                  clearable
                />
                <HeliosBrowseFilterControls modalOpen={open} columns={data?.columns ?? []} browseFilters={browseFilters} />
              </Box>
            }
            defaultSelectedRows={currentSelection.map((row) => row.id?.toString())}
            isMultiSelect={allowMultipleSelection}
            selectOnRowClick
          />
        </Box>
      </ActionModal>
    );
  }
) as <K extends FieldValues>(props: Props<K>) => JSX.Element;
